import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getProductTagsTrends } from '../services/catalog-operations.service';
import type { ProductTagsTrendsScope } from '../types/catalog-operations.api.types';
import { isExperimentSource } from '../utils/batch-flow-icons';

/** Tags-trends scope for a batch page: experiment batches are keyed by experiment id
 *  (their rows carry `experiment_id`, not a job id), every other batch by job id. */
export function tagsTrendsScope(batchId: string, triggerSource: string): ProductTagsTrendsScope {
  return isExperimentSource(triggerSource)
    ? { experimentId: batchId }
    : { enrichmentJobId: batchId };
}

/** Fetches tags and trends for the current page of products in one batch call.
 *  Disabled until both the scope and a non-empty product id list are available.
 *  Returns a Map<productId, {tags, trends}> — empty map while loading. */
export function useProductTagsTrends(
  scope: ProductTagsTrendsScope | undefined,
  productIds: string[],
) {
  const [kind, id] = !scope
    ? (['job', ''] as const)
    : 'experimentId' in scope
      ? (['experiment', scope.experimentId] as const)
      : (['job', scope.enrichmentJobId] as const);
  return useQuery({
    queryKey: queryKeys.catalogOperations.productTagsTrends(kind, id, productIds),
    queryFn: () => getProductTagsTrends(scope ?? { enrichmentJobId: '' }, productIds),
    enabled: Boolean(id) && productIds.length > 0,
    staleTime: 5 * 60 * 1_000,
  });
}
